import {
  takeEvery,
  put,
  take,
  call,
  fork,
  getContext,
} from 'redux-saga/effects';
import { disableFirebaseLocalStorage } from 'utils/firebase';

import {
  SYNC_AFTER_LOGIN,
  FIREBASE_READY,
  CHECK_LOGIN_STATUS,
} from 'constants/actionTypes';
import {
  setFirebaseAuthErrorStatus,
  syncAccountWithFirebase,
  updateFirebaseMetadata,
  syncAfterLogin,
} from 'actions/firebase';
import { syncTopLevelDataWithFirebase } from 'actions/topLevelData';

import { checkForChromeExtension } from 'sagas/chromeExtension';
import {
  addToErrReportContext,
  addToErrReportTags,
  reportError,
} from 'helpers/error-reporting';
import { syncUser, USER_REPLACED } from 'actions/user';
import { initialize as initializeAccountSwitch } from 'sagas/accounts';

/**
 * Determines if there is an active session, and if so
 * jumpstarts Firebase
 */
export function* checkLoginStatus() {
  try {
    const auth = yield getContext('auth');
    const user = yield call(auth.login);
    yield call(initializeAccountSwitch);
    yield call(disableFirebaseLocalStorage);
    yield call(checkForChromeExtension);
    yield put(syncAfterLogin(user));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    reportError(error);
  }
}

/**
 *
 * @param {User} user User obj returned by auth client
 */

export function* initUserSync({ payload: { user } }) {
  try {
    const auth = yield getContext('auth');
    const isSpoofing = yield call(auth.isSpoofing);
    let uid = yield call(auth.getOriginalUserId);

    uid = uid || user.getUserId();

    if (uid.includes('simplelogin')) {
      uid = uid.replace('simplelogin:', '');
    }

    yield put(syncUser(uid));
    yield take(USER_REPLACED);

    const authDataToStore = {
      uid,
      token: user.getToken(),
      idToken: user.getIdToken(),
    };

    yield put(
      updateFirebaseMetadata({
        auth: authDataToStore,
      })
    );

    // calling this with no value apparently clears any error stored
    yield put(setFirebaseAuthErrorStatus());
    yield put(syncTopLevelDataWithFirebase());

    yield put({
      type: FIREBASE_READY,
    });
    if (!isSpoofing) {
      addToErrReportContext({
        userId: uid,
      });
      addToErrReportTags({
        userId: uid,
      });
    }
  } catch (error) {
    yield put(setFirebaseAuthErrorStatus(error.message));
  }
}

/**
 * Begin syncing the first accountId listed in `/users/${userId}/accounts`, or
 * the `accountId` if provided, with our local reducers.
 *
 * @param action
 */

export function* initAccountSync() {
  try {
    const auth = yield getContext('auth');
    const accountId = yield call(auth.getAccountId);
    const isSpoofing = yield call(auth.isSpoofing);

    yield put(syncAccountWithFirebase(accountId));

    if (!isSpoofing) {
      addToErrReportContext({
        accountId,
      });
      addToErrReportTags({
        accountId,
      });
    }
  } catch {
    yield put(setFirebaseAuthErrorStatus('No Appcues account found for user'));
  }
}

function* authSaga() {
  yield takeEvery(CHECK_LOGIN_STATUS, checkLoginStatus);
  yield takeEvery(SYNC_AFTER_LOGIN, initUserSync);
  yield takeEvery(SYNC_AFTER_LOGIN, checkForChromeExtension);

  yield take(FIREBASE_READY);
  yield fork(initAccountSync);
}

export default authSaga;
